import { Alert, Button, Spin, Tag } from 'antd';
import styled from 'styled-components';
import { CheckOutlined, SafetyOutlined } from '@ant-design/icons';
import { semanticTheme } from '@tools/ui/dime';

export const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding: 24px 20px;
`;

export const ContentWrapper = styled.div`
  max-width: 780px;
  margin: 0 auto;
`;

export const StyledBackButton = styled(Button)`
  && {
    padding: 0;
    margin-bottom: 20px;
    font-size: ${semanticTheme.dime.fonts.sizes.sm};
  }
`;

export const HeaderSection = styled.div`
  margin-bottom: 28px;
`;

export const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: ${semanticTheme.dime.spacing.s};
`;

export const StyledHeaderIcon = styled(SafetyOutlined)`
  font-size: ${semanticTheme.dime.fonts.sizes.lg};
  color: #9254de;
`;

export const PageTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: ${semanticTheme.dime.fonts.weights.semiBold};
  color: #141414;
`;

export const PageSubtitle = styled.p`
  margin: 0;
  color: #8c8c8c;
  font-size: ${semanticTheme.dime.fonts.sizes.sm};
`;

export const WarningText = styled.p`
  margin: 6px 0 0;
  color: #ff4d4f;
  font-size: ${semanticTheme.dime.fonts.sizes.sm};
  font-weight: 700;
`;

export const FieldGroup = styled.div`
  margin-bottom: ${semanticTheme.dime.spacing.l};
`;

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: ${semanticTheme.dime.fonts.sizes.xs};
  font-weight: ${semanticTheme.dime.fonts.weights.semiBold};
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const RequiredMark = styled.span`
  color: #ff4d4f;
`;

export const FieldRow = styled.div`
  display: flex;
  gap: ${semanticTheme.dime.spacing['2m']};
  margin-bottom: ${semanticTheme.dime.spacing.l};
`;

export const EntityTypeCol = styled.div`
  flex: 0 0 160px;
`;

export const FlexCol = styled.div`
  flex: 1;
`;

export const ExecutionTypeCol = styled.div`
  flex: 0 0 180px;
`;

export const PartnerCol = styled.div`
  flex: 0 0 140px;
`;

export const FieldInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  height: 32px;
  padding: ${semanticTheme.dime.spacing.s} 11px;
  border-radius: 6px;
  border: 1px solid ${({ $error }) => ($error ? '#ff4d4f' : '#d9d9d9')};
  font-size: ${semanticTheme.dime.fonts.sizes.sm};
  outline: none;
  background: #fff;
  color: #262626;
  cursor: text;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus {
    border-color: ${({ $error }) => ($error ? '#ff4d4f' : '#9254de')};
  }
  &:disabled {
    background: #fafafa;
    color: #8c8c8c;
    cursor: not-allowed;
  }
`;

export const TriggerSection = styled.div`
  margin-bottom: 28px;
`;

export const StyledTriggerButton = styled(Button)<{ $active?: boolean }>`
  ${({ $active }) =>
    $active &&
    `
    border-color: #9254de;
    color: #9254de;
  `}
`;

export const TriggeredCard = styled.div`
  margin-bottom: ${semanticTheme.dime.spacing.l};
  padding: ${semanticTheme.dime.spacing['2m']} ${semanticTheme.dime.spacing.l};
  background: #fff7e6;
  border-radius: 8px;
  border: 1px solid #ffd591;
`;

export const TriggeredLabel = styled.div`
  font-size: 11px;
  font-weight: ${semanticTheme.dime.fonts.weights.semiBold};
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${semanticTheme.dime.spacing.m};
`;

export const StyledInlineSpinner = styled(Spin)`
  margin-left: ${semanticTheme.dime.spacing.m};
`;

export const StyledCheckIcon = styled(CheckOutlined)`
  margin-left: ${semanticTheme.dime.spacing.m};
  color: #9254de;
`;

export const TriggeredItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${semanticTheme.dime.spacing.m};
  margin-bottom: ${semanticTheme.dime.spacing.s};
`;

export const StyledMonoTag = styled(Tag)`
  font-family: monospace;
  font-size: ${semanticTheme.dime.fonts.sizes.xs};
`;

export const FlowIdText = styled.span`
  font-size: 11px;
  color: #8c8c8c;
`;

export const PollInfo = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: #bfbfbf;
`;

export const ResultItem = styled.div`
  margin-bottom: ${semanticTheme.dime.spacing.l};
`;

export const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${semanticTheme.dime.spacing.m};
  margin-bottom: ${semanticTheme.dime.spacing.m};
`;

export const AgentNameLabel = styled.span`
  font-size: 11px;
  font-weight: ${semanticTheme.dime.fonts.weights.semiBold};
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const StyledSmallTag = styled(Tag)`
  font-size: 10px;
  line-height: 16px;
  padding: 0 6px;
`;

export const ResultCard = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-left: 3px solid #9254de;
  border-radius: 8px;
  padding: 20px ${semanticTheme.dime.spacing.xl};
  font-size: ${semanticTheme.dime.fonts.sizes.sm};
  line-height: 1.75;
  color: #262626;
`;

export const FieldError = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #ff4d4f;
`;

export const ErrorCard = styled.div`
  padding: 14px ${semanticTheme.dime.spacing.l};
  background: #fff2f0;
  border-radius: 8px;
  border: 1px solid #ffccc7;
  border-left: 3px solid #ff4d4f;
  color: #cf1322;
  font-size: 13px;
`;

export const LoadingCard = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 14px ${semanticTheme.dime.spacing.l};
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  border-left: 3px solid #9254de;
  color: #8c8c8c;
  font-size: 13px;
`;

export const StyledAlert = styled(Alert)`
  margin-bottom: ${semanticTheme.dime.spacing.l};
`;

export const StyledRerunButton = styled(Button)`
  margin-top: ${semanticTheme.dime.spacing.s};
`;
